import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgressIndicator } from '@sorare/core/src/atoms/loader/CircularProgressIndicator';

export type GuessTheScoreTaskLineupProgressState =
  | 'idle'
  | 'under'
  | 'over'
  | 'exact';

const Root = styled.div`
  position: relative;
`;

const BadgeIcon = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 16px;
  height: 16px;
  border-radius: 8px;
  color: var(--c-white);

  &.exact {
    background: var(--c-score-high);
  }
  &.failure {
    background: var(--c-score-veryLow);
  }
`;

const STATE_COLORS: Record<
  GuessTheScoreTaskLineupProgressState,
  { from: string; to?: string }
> = {
  idle: { from: 'var(--c-nd-200)' },
  under: { from: 'var(--c-red-400)' },
  over: { from: 'var(--c-red-400)' },
  exact: { from: 'var(--c-score-high)' },
};

type Props = {
  state: GuessTheScoreTaskLineupProgressState;
  /** Predicted final score the user is targeting. */
  targetScore?: number | null;
  /** Current live score; used to derive the ring fill for the `under` state. */
  score?: number | null;
};

export const getProgress = (
  score: number | null | undefined,
  targetScore: number | null | undefined
): number => {
  if (!targetScore || targetScore <= 0) return 0;
  return Math.min(Math.max((score ?? 0) / targetScore, 0), 1);
};

export const GuessTheScoreTaskLineupProgress = ({
  state,
  targetScore,
  score,
}: Props) => {
  const progress = getProgress(score, targetScore);

  return (
    <Root>
      <CircularProgressIndicator
        size={6}
        progresses={[progress]}
        colors={[STATE_COLORS[state]]}
        text={targetScore ? `${targetScore}` : '-'}
      />
      {state === 'exact' && (
        <BadgeIcon className="exact">
          <FontAwesomeIcon icon={iconCheck} size="xs" />
        </BadgeIcon>
      )}
      {(state === 'over' || state === 'under') && (
        <BadgeIcon className="failure">
          <FontAwesomeIcon icon={iconX} size="xs" />
        </BadgeIcon>
      )}
    </Root>
  );
};
